<template>
  <PageLayout>
    <template #header>
      <ClientSelection  v-if="isMSMUser"
                        :disabled="appLoading"
                        @clientselectionchanged="onClientSelectionChanged"/>
      <generic-page-header class="my-2 border-0">
        <template #title>{{ $t('Webportal.SalesCockpit') }}</template>
        <template #description>{{ $t('Webportal.SalesCockpitDescription') }}</template>
        <template #actions-top>
          <BButton
            variant="outline-secondary"
            size="sm"
            :disabled="disabled"
            @click="handle($event, 'info')">
            <Info :size="16" />
          </BButton>

          <BButton  variant="outline-secondary"
                    size="sm"
                    :disabled="disabled"
                    @click="handle($event, 'export')">
            <Download :size="16" class="me-2" />
            {{ $t('Webportal.Export') }}
          </BButton>

          <BButton  variant="outline-secondary"
                    size="sm"
                    :disabled="disabled"
                    @click="handle($event, 'share')">
            <Share2 :size="16" class="me-2" />
            {{ $t('Webportal.Share') }}
          </BButton>
        </template>
        <template #actions-bottom>

          <BButton
            variant="outline-secondary"
            size="sm"
            :disabled="disabled"
            @click="handle($event, 'save')">
            <Save :size="16" class="me-2" />
            {{ $t('Webportal.SaveView') }}
          </BButton>

          <!-- Neue Komponente statt BFormSelect -->
          <SavedViewSelection v-model="selectedView"
                              style="width: 300px;"
                              :disabled="disabled"
                              :placeholder="$t('Webportal.ChangeView')"
                              @create="handleCreateView" />
        </template>
      </generic-page-header>
    </template>

    <!-- Main Content -->
    <template #main>
      <DetailFilterRow  @filter-click="handleFilterClick"
                        @filter-changed="handleFilterChanged"
                        class="my-2" />
      <LegendList       class="my-3" />

      <!-- Sales Cockpit - Cluster -->
      <CustomCard class="mt-4" v-if="columns && columns.length > 0">
        <template #header-title>
          Sales Cockpit - Cluster
        </template>
        <template #header-subtitle>
          Overview of sales performance and key metrics.
        </template>
        <template #header-actions-top>
          <div>
            <BButton  variant="outline-secondary"
                      size="sm"
                      @click="handleExpandCollapseAll('salesPerformanceExpanded')">
            <ChevronsLeft v-if="salesPerformanceExpanded" :size="16" class="me-1" />
            <ChevronsRight v-else :size="16" class="me-1" />
            {{ salesPerformanceExpanded ? $t('Webportal.CollapseAll') : $t('Webportal.ExpandAll') }}
          </BButton>
          </div>
        </template>
        <template #header-actions-bottom>
          <BButton  variant="outline-secondary"
                    :disabled="true"
                    size="sm">
            <Save :size="16" class="me-2" />
            {{ $t('Webportal.SaveView') }}
          </BButton>

          <!-- Neue Komponente statt BFormSelect -->
          <SavedViewSelection v-model="selectedView"
                              :disabled="true"
                              style="width: 300px;"
                              :placeholder="$t('Webportal.ChangeView')"
                              @create="handleCreateView" />
        </template>

        <KpiColumnBoard @toggle-column-expanded="handleColumnToggle"
                        :columns="columns"
                        id="clusterBoard" />
      </CustomCard>

       <!-- Sales Cockpit - KPI -->
      <CustomCard class="mt-4" v-if="kpiColumns && kpiColumns.length > 0">
        <template #header-title>
          Sales Cockpit - KPI
        </template>
        <template #header-subtitle>
          Overview of sales performance and key metrics.
        </template>
        <template #header-actions-top>
          <div>
            <BButton  variant="outline-secondary"
                      size="sm"
                      @click="handleExpandCollapseAll('salesPerformanceKpiExpanded')">
            <ChevronsLeft v-if="salesPerformanceKpiExpanded" :size="16" class="me-1" />
            <ChevronsRight v-else :size="16" class="me-1" />
            {{ salesPerformanceKpiExpanded ? $t('Webportal.CollapseAll') : $t('Webportal.ExpandAll') }}
          </BButton>
          </div>
        </template>

        <KpiColumnBoard @toggle-column-expanded="handleColumnToggle"
                        :columns="kpiColumns"
                        id="kpiBoard" />
      </CustomCard>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { gatherFilterIds } from '@/utils/treeHelpers';
import CancelToken from '@/mixins/CancelToken';
import DashboardService from '@/services/DashboardService-APP_TARGET';

import DetailFilterRow from '@/componentsv3/DetailFilterRow.vue';
import LegendList from '@/componentsv3/LegendList.vue';
import PageLayout from '@/componentsv3/PageLayout.vue';
import PageSidebar from '@/componentsv3/PageSidebar.vue';
import GenericPageHeader from '@/componentsv3/GenericPageHeader.vue';
import KpiColumnBoard from '@/componentsv3/KpiColumnBoard/KpiColumnBoard.vue';
import KpiBoard from '@/componentsv3/KpiBoard/KpiBoard.vue';
import CustomCard from '@/componentsv3/CustomCard.vue';
import SavedViewSelection from '@/componentsv3/SavedViewSelection.vue';
import ClientSelection from '@/components/ClientSelection.vue';

import { Info,ChevronsLeft, ChevronsRight, Save, Download, Share2 } from 'lucide-vue-next';

export default {
  name: 'SalesCockpit',
  mixins: [CancelToken],
  components: {
    CustomCard,
    ClientSelection,
    DetailFilterRow,
    KpiColumnBoard, KpiBoard,
    LegendList,
    Info, Download, Share2, ChevronsLeft, ChevronsRight, Save,
    GenericPageHeader,
    PageLayout,
    PageSidebar,
    SavedViewSelection,

    ChevronsLeft, ChevronsRight, Save,
  },

  data() {
    return {
      columns: [],
      dashboardData: null,
      dashboardDataCancelToken: null,
      salesPerformanceExpanded: false,
      salesPerformanceKpiExpanded: false,
      selectedView: null,
      kpiColumns: [],
    }
  },

  computed: {
    ...mapGetters('auth', [ 'clientId', 'isMSMUser' ]),
    ...mapGetters('clientData', [ 'appLoading', 'brandIds', 'filters', 'isClientReady', 'surveyIds', 'structure' ]),
    ...mapGetters('localization', [ 'currentLocale' ]),
  },

  async mounted() {
    EventBus.$on('app.ready', this.loadOverviewData);
    if (this.isClientReady && this.filters && !this.dashboardData) {
      this.loadOverviewData();
    }
  },

  beforeUnmount() {
    EventBus.$off('app.ready', this.loadOverviewData);
    this.cancelCancelToken(this.dashboardDataCancelToken);
    this.setAppLoading(false);
  },

  methods: {
    ...mapActions('clientData', [ 'fetchFilters', 'setAppLoading' ]),

    getFilterSettings() {
      const filter = {
        api: APP_TARGET === 'bmw' ? 'bmw2021experimental' : undefined,
        brand: APP_TARGET === 'bmw' ? this.brandIds?.join('|') : undefined,
        clientId: this.clientId,
        projects: null,
        waves: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.Waves).join(',') : this.filters?.waves?.filter(x => x.checked).map(x => x.id).join(','),
        regions: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.Regions).join('|') : this.filters?.regions?.filter(x => x.checked).map(x => x.id).join('|'),
        subregions: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.Subregions).join('|') : this.filters?.Subregions?.filter(x => x.checked).map(x => x.id).join('|'),
        nscs: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.NSCs).join('|') : this.filters?.NSCs?.filter(x => x.checked).map(x => x.id).join('|'),
        outlets: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.ClientLocations).join(',') : this.filters?.ClientLocations?.filter(x => x.checked).map(x => x.id).join(','),
        nscsub: this.filters?.SubNSCs ? gatherFilterIds(this.filters?.SubNSCs).join('|') : null,
        areas: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.Areas).join('|') : this.filters?.Areas?.filter(x => x.checked).map(x => x.id).join('|'),
        dealergroups: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.Dealergroups).join('|') : this.filters?.Dealergroups?.filter(x => x.checked).map(x => x.id).join('|'),
        contactinitiation: null,
        surveys: APP_TARGET === 'bmw' ? gatherFilterIds(this.filters?.Surveys).join(',') : this.filters?.surveys?.filter(x => x.checked).map(x => x.id).join(','),
        page: 'salescockpit',
      };

      if (!filter.surveys || filter.surveys.length === 0) {
        filter.surveys = [...new Set(
          this.structure.children.flatMap(item =>
            item.Survey ? item.Survey.SurveyId : (item.children?.map(child => child.Survey?.SurveyId) || [])
          ))].join(',');
      }
      return filter;
    },

    async loadOverviewData() {
      if (this.appLoading || !this.isClientReady || !this.filters || !this.structure) {
        return;
      }

      this.setAppLoading(true);
      const filter = this.getFilterSettings();
      this.dashboardDataCancelToken = this.initCancelToken(this.dashboardDataCancelToken);

      await DashboardService.getDashboard(filter, this.dashboardDataCancelToken, this.currentLocale)
        .then(response => {
          this.dashboardData = response?.data;
          this.columns = this.getClusters();
          this.kpiColumns = this.getKpiColumns();
        })
        .catch(error => {
          log.error('FAIL getDashboard:', error);
        }).finally(() => {
          this.setAppLoading(false);
        });
    },

    getClusters() {
      if (!this.dashboardData || !this.dashboardData.Question || !this.dashboardData.Cluster) {
        return [];
      }
      let columns = this.dashboardData.Cluster.filter(x => !x.Code.startsWith('0. ') ).map(g => {
        return {
          id: g.Code,
          label: g.Name,
          isExpanded: this.salesPerformanceExpanded,
          summary: {
            label: this.dashboardData.OverallByWave[0].Name,
            SurveyWave: this.dashboardData.OverallByWave[0].SurveyWave
          },
          SurveyWave: g.SurveyWave,
          waves: this.dashboardData.OverallByWave[0].SurveyWave,
          kpis: this.dashboardData.Question
            .filter(kpi => kpi.Code === g.Code)
            .map(kpi => ({
              id: kpi.Code,
              label: kpi.Name || kpi.QuestionText,
              SurveyWave: kpi.SurveyWave
            }))
        };
      });
      return columns;
    },

    getKpiColumns() {
      if (!this.dashboardData || !this.dashboardData.KPI || !this.dashboardData.KPIGroup) {
        return [];
      }
      let columns = this.dashboardData.KPIGroup.map(g => {
        return {
          id: APP_TARGET === 'bmw' ? g.KpiCode : g.Code,
          label: APP_TARGET === 'bmw' ? g.KpiName : g.Name,
          KpiCode:  APP_TARGET === 'bmw' ? g.KpiCode : g.Code,
          KpiName:  APP_TARGET === 'bmw' ? g.KpiName : g.Name,
          isExpanded: this.salesPerformanceExpanded,
          summary: {
            label: this.dashboardData.OverallByWave[0].Name,
            SurveyWave: this.dashboardData.OverallByWave[0].SurveyWave
          },
          SurveyWave: g.SurveyWave,
          YTD: g.YTD,
          waves: this.dashboardData.OverallByWave[0].SurveyWave,
          kpis: this.dashboardData.KPI
            .filter(kpi => kpi.KpiGroupCode === g.KpiCode ||  kpi.KpiGroupCode === g.KpiCode)
            .map(kpi => ({
              id: APP_TARGET === 'bmw' ? kpi.KpiCode : kpi.Code,
              label: APP_TARGET === 'bmw' ? kpi.KpiName : kpi.Name,
              KpiCode: APP_TARGET === 'bmw' ? kpi.KpiCode : kpi.Code,
              KpiGroupCode: kpi.KpiGroupCode,
              KpiName: APP_TARGET === 'bmw' ? kpi.KpiName : kpi.Name,
              KpiNameShort: kpi.KpiNameShort,
              SurveyWave: kpi.SurveyWave,
              YTD: kpi.YTD
            }))
        };
      });

      return columns;
    },

    async onClientSelectionChanged(clientSettings) {
      if (this.appLoading || !this.isClientReady) return;
      this.loadOverviewData();
    },

    handleFilterChanged(filter, filters) {

      // DashboardService.getOptions()
      this.loadOverviewData();
    },

    handleFilterClick(filter) {
      if (filter.key === 'refresh') {
        this.loadOverviewData();
      }
    },

    handleColumnToggle({ controlId, columnId }) {
      const collection = controlId === 'clusterBoard' ? this.columns : this.kpiColumns;
      const column = collection.find(c => c.label === columnId);
      if (column) {
        column.isExpanded = !column.isExpanded;
      }
    },
    handleExpandCollapseAll(switchName) {
      this[switchName] = !this[switchName];
      let collection = switchName === 'salesPerformanceExpanded' ? this.columns : this.kpiColumns;
      collection.forEach(column => {
        column.isExpanded = this[switchName];
      });
    },

    handleSeriesChange(selectedValues) {
      log.log('Ausgewählte Serien:', selectedValues);
      // API Call oder Filter anwenden
    },

    handleCreateView() {
      log.log('Neue Ansicht erstellen mit:', this.selectedView);
      // Logik zum Erstellen einer neuen Ansicht hier einfügen
    },

    handle(ev, action) {
      console.warn(action, ev);
      this.$emit(action);
    },

    handleCreateView() {
      this.$emit('create-view');
    }
  }
}
</script>
